import React, { useState, useEffect } from "react";
import { StyleSheet, View } from "react-native";
import {
  PlaceModel,
  ShareModel,
  ContactModel,
  GroupModel,
  MemoryModel,
  MediaModel,
  ICustomCardEntityConfig,
  formatCardTimestamp,
  formatDisplayDate,
} from "common247";
import { Ionicons } from "@expo/vector-icons";
import { ShareSelector } from "../sharing/ShareSelector";
import { useThemeColor } from "../../hooks/useThemeColor";
import { ThemedKebabMenu, type IKebabActionItem } from "../ThemedKebabMenu";
import { ThemedListViewMode } from "../ThemedList";
import { ThemedChip } from "../ThemedChip";
import { ThemedCompactCard } from "../cards/ThemedCompactCard";
import { ThemedMediumCard } from "../cards/ThemedMediumCard";
import { ThemedFullCard } from "../cards/ThemedFullCard";
import { ThemedCustomCard } from "../cards/ThemedCustomCard";
import {
  resolveCardLinkedContacts,
  resolveCardLinkedGroups,
  resolveCardLinkedMedia,
  resolveCardLinkedMemories,
  resolveCardLinkedPlaces,
  resolveCardLinkedAnchors,
} from "../cards/card-helpers";
import TagListStripDefault, { TagListStrip as TagListStripNamed } from "@/components/tags/TagListStrip";
const TagListStrip = TagListStripNamed || TagListStripDefault;

/**
 * Props for the PlaceCard component.
 */
export interface IPlaceCardProps {
  /** The Place data model */
  place: PlaceModel;
  /** Whether tag editing is permitted */
  canEditTags?: boolean;
  /** Whether to show the owner display name */
  showOwner?: boolean;
  /** Whether this is an administrative view */
  isAdminView?: boolean;
  /** Current authenticated user ID */
  currentUserId?: string;
  /** Compact density override */
  compact?: boolean;
  /** Presentation tier: chip, compact, medium, full, or custom */
  viewMode?: ThemedListViewMode;
  /** Hover state indicator */
  isHovered?: boolean;
  /** Selection mode */
  selectionMode?: boolean;
  /** Whether this card is selected */
  isSelected?: boolean;
  /** Primary card press callback */
  onPress?: () => void;
  /** Long press callback */
  onLongPress?: () => void;
  /** Web context menu callback */
  onContextMenu?: (e?: any) => void;
  /** Edit action callback */
  onEditPress?: () => void;
  /** Inspect action callback */
  onInspect?: () => void;
  /** Share action callback */
  onShare?: () => void;
  /** Delete action callback */
  onDelete?: () => void;

  /** Existing shares associated with this place */
  shares?: ShareModel[];
  /** Available contacts for sharing picker */
  availableContacts?: ContactModel[];
  /** Available groups for sharing picker */
  availableGroups?: GroupModel[];
  /** Available memories */
  availableMemories?: MemoryModel[];
  /** Available media */
  availableMedia?: MediaModel[];
  /** Available places */
  availablePlaces?: PlaceModel[];
  /** Available anchors */
  availableAnchors?: any[];
  /** Loading shares flag */
  isLoadingShares?: boolean;
  /** Callback to share with a target */
  onShareTarget?: (targetId: string, targetType: "contact" | "group", role: "viewer" | "editor") => void;
  /** Callback to revoke a share */
  onUnshare?: (shareId: string) => void;
  /** Controlled sharing tray expansion state */
  isSharingExpanded?: boolean;
  /** Toggle sharing tray callback */
  onToggleSharing?: () => void;
  /** Custom card entity configuration */
  config?: ICustomCardEntityConfig;
}

/**
 * First-class PlaceCard component adhering to the 5-tier card architecture.
 * Features:
 * - chip: purple chip token
 * - compact: cover thumbnail, title, address snippet, favourite rating
 * - medium: compact + date, location coordinates badge, linked chips tray
 * - full: partitioned ledger folio with complete details and auto-expanded media highlights
 * - custom: dynamic schema-driven card based on ICustomCardEntityConfig
 */
export const PlaceCard: React.FC<IPlaceCardProps> = ({
  place,
  canEditTags,
  showOwner = false,
  isAdminView = false,
  currentUserId,
  compact = false,
  viewMode,
  isHovered,
  selectionMode = false,
  isSelected = false,
  onPress,
  onLongPress,
  onContextMenu,
  onEditPress,
  onInspect,
  onShare,
  onDelete,
  shares,
  availableContacts,
  availableGroups,
  availableMemories,
  availableMedia,
  availablePlaces,
  availableAnchors,
  isLoadingShares,
  onShareTarget,
  onUnshare,
  isSharingExpanded,
  onToggleSharing,
  config,
}) => {
  const userRole = place.userRole || "owner";
  const isOwner = userRole === "owner";
  const isViewer = userRole === "viewer";
  const canEdit = isOwner || userRole === "editor";
  const isFiveStar = place.favouriteRating === 5;

  const [localExpanded, setLocalExpanded] = useState(false);
  const isExpanded = isSharingExpanded !== undefined ? isSharingExpanded : localExpanded;

  const [lastKnownShares, setLastKnownShares] = useState<ShareModel[] | null>(null);

  useEffect(() => {
    if (shares !== undefined) {
      setLastKnownShares(shares);
    }
  }, [shares]);

  const handleSharePress = (e?: any) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (onToggleSharing) {
      onToggleSharing();
    } else if (onShareTarget || shares !== undefined) {
      setLocalExpanded(!localExpanded);
    } else if (onShare) {
      onShare();
    } else {
      setLocalExpanded(!localExpanded);
    }
  };

  const effectiveShares: ShareModel[] =
    shares ??
    lastKnownShares ??
    (place.sharedWith?.map((s) => ({
      id: s.id,
      sourceId: place.id || "",
      sourceType: "place" as const,
      targetId: s.id,
      targetType: s.type,
      role: s.role,
      targetDisplayName: s.displayName,
      targetUserId: s.targetUserId,
      sharedBy: place.ownerId || "",
      sharedAt: place.createdAt,
    })) as ShareModel[]) ??
    [];

  const canShowShareButton = place.canShare && (onShare || onShareTarget || onToggleSharing || shares !== undefined);

  const kebabActions: IKebabActionItem[] = [];
  if (canShowShareButton) {
    kebabActions.push({
      id: "share",
      label: isExpanded ? "Close Sharing" : "Manage Sharing",
      icon: "share-social-outline",
      onPress: handleSharePress,
    });
  }
  if (canEdit && onEditPress) {
    kebabActions.push({
      id: "edit",
      label: "Edit Place",
      icon: "pencil-outline",
      onPress: onEditPress,
    });
  }
  if (isOwner && onDelete) {
    kebabActions.push({
      id: "delete",
      label: "Delete Place",
      icon: "trash-outline",
      destructive: true,
      onPress: onDelete,
    });
  }

  // 1. Chip tier
  if (viewMode === "chip") {
    return (
      <View style={{ margin: 2 }}>
        <ThemedChip
          entityType="place"
          id={place.id}
          label={place.name || "Place"}
          isOwner={isOwner}
          isFiveStar={isFiveStar}
          onPress={onPress}
          onLongPress={onLongPress}
        />
      </View>
    );
  }

  const resolvedDate = place.date ? formatDisplayDate(place.date) : undefined;
  const formattedDate = resolvedDate || (place.createdAt ? `Created: ${formatCardTimestamp(place.createdAt)}` : undefined);

  const resolvedMemories = resolveCardLinkedMemories(place);
  const resolvedMedia = resolveCardLinkedMedia(place, availableMedia);
  const resolvedGroups = resolveCardLinkedGroups(place, availableGroups);
  const resolvedContacts = resolveCardLinkedContacts(place, availableContacts);
  const resolvedPlaces = resolveCardLinkedPlaces(place, availablePlaces);
  const resolvedAnchors = resolveCardLinkedAnchors(place, availableAnchors);

  // Address subtitle
  const addressSubtitle = place.address
    ? place.address
    : place.latitude !== undefined && place.longitude !== undefined
    ? `📍 ${place.latitude.toFixed(4)}, ${place.longitude.toFixed(4)}`
    : undefined;

  // 2. Medium tier
  if (viewMode === "medium") {
    return (
      <ThemedMediumCard
        entityType="place"
        id={place.id}
        title={place.name || "Place"}
        formattedDate={formattedDate}
        description={place.description || addressSubtitle}
        ownerDisplayName={place.ownerDisplayName}
        isOwner={isOwner}
        isFiveStar={isFiveStar}
        isShared={!isOwner}
        kebabActions={kebabActions}
        selectionMode={selectionMode}
        isSelected={isSelected}
        onPress={onPress}
        onLongPress={onLongPress}
        onContextMenu={onContextMenu}
        contactsList={resolvedContacts}
        groupsList={resolvedGroups}
        memoriesList={resolvedMemories}
        mediaList={resolvedMedia}
        placesList={resolvedPlaces}
        anchorsList={resolvedAnchors}
        tagsList={place.tags ? Object.values(place.tags) : []}
        sharesList={effectiveShares}
      >
        {isExpanded && canShowShareButton && (
          <View style={styles.sharingTray}>
            <ShareSelector
              shares={effectiveShares}
              availableContacts={availableContacts as any}
              availableGroups={availableGroups}
              isLoading={isLoadingShares}
              onShare={onShareTarget || (() => {})}
              onUnshare={onUnshare || (() => {})}
              canEdit={canEdit}
            />
          </View>
        )}
      </ThemedMediumCard>
    );
  }

  // 3. Full tier
  if (viewMode === "full") {
    return (
      <View style={styles.fullCardWrapper}>
        <ThemedFullCard
          entityType="place"
          id={place.id}
          title={place.name || "Place"}
          formattedDate={formattedDate}
          description={place.description}
          ownerDisplayName={place.ownerDisplayName}
          isOwner={isOwner}
          isFiveStar={isFiveStar}
          userRole={userRole}
          kebabActions={kebabActions}
          onPress={onPress}
          onEditPress={onEditPress}
          onDeletePress={onDelete}
          contactsList={resolvedContacts}
          groupsList={resolvedGroups}
          memoriesList={resolvedMemories}
          mediaList={resolvedMedia}
          placesList={resolvedPlaces}
          anchorsList={resolvedAnchors}
          tagsList={place.tags ? Object.values(place.tags) : []}
          sharesList={effectiveShares}
        />
        {isExpanded && canShowShareButton && (
          <View style={styles.sharingTray}>
            <ShareSelector
              shares={effectiveShares}
              availableContacts={availableContacts as any}
              availableGroups={availableGroups}
              isLoading={isLoadingShares}
              onShare={onShareTarget || (() => {})}
              onUnshare={onUnshare || (() => {})}
              canEdit={canEdit}
            />
          </View>
        )}
      </View>
    );
  }

  // 4. Custom tier
  if (viewMode === "custom") {
    return (
      <ThemedCustomCard
        entityType="place"
        item={place as any}
        title={place.name || "Place"}
        dateStr={formattedDate || ""}
        description={place.description || addressSubtitle || ""}
        isOwner={isOwner}
        isFiveStar={isFiveStar}
        kebabActions={kebabActions}
        selectionMode={selectionMode}
        isSelected={isSelected}
        onPress={onPress}
        onLongPress={onLongPress}
        onContextMenu={onContextMenu}
        placesList={resolvedPlaces}
        config={config}
      />
    );
  }

  // 5. Compact tier (default)
  return (
    <ThemedCompactCard
      entityType="place"
      id={place.id}
      title={place.name || "Place"}
      date={formattedDate}
      isOwner={isOwner}
      isFiveStar={isFiveStar}
      selectionMode={selectionMode}
      isSelected={isSelected}
      onPress={onPress}
      onLongPress={onLongPress}
      onContextMenu={onContextMenu}
      kebabActions={kebabActions}
    />
  );
};

const styles = StyleSheet.create({
  fullCardWrapper: {
    marginBottom: 8,
  },
  sharingTray: {
    padding: 12,
    marginTop: 8,
    borderRadius: 8,
  },
});
